"use client";

import { Field as FieldPrimitive } from "@base-ui/react/field";
import { mergeProps } from "@base-ui/react/merge-props";
import type * as React from "react";
import { cn } from "@/lib/utils";

export type TextareaProps =
  & React.ComponentPropsWithoutRef<"textarea">
  & React.RefAttributes<HTMLTextAreaElement>
  & {
    size?: "sm" | "default" | "lg" | number;
    unstyled?: boolean;
  };

/**
 * Coss Textarea primitive. Mirrors the structure of `Input.tsx`: a styled
 * `<span data-slot="textarea-control">` wrapper around a real `<textarea>`,
 * rendering via Base UI's `Field.Control` so the wrapper participates in
 * FieldLabel / FieldDescription / FieldError relationships when present.
 *
 * Why Base UI here (vs the simpler `<textarea>` the legacy `TextInput` used):
 * it lets a `Field` ancestor drive `disabled` / `aria-invalid` / `name` /
 * `id` from one source, the same pattern `Input` already uses. Keeping the
 * same `data-slot="textarea-control"` + `data-slot="textarea"` split lets
 * the wrapper-style tailwind selectors stay stable for consumer styling.
 */
export function Textarea({
  className,
  size = "default",
  unstyled = false,
  ref,
  ...props
}: TextareaProps): React.ReactElement {
  return (
    <span
      className={cn(
        !unstyled &&
          "relative inline-flex w-full rounded-lg border border-input bg-background not-dark:bg-clip-padding text-base text-foreground shadow-xs/5 ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-ring has-disabled:opacity-64 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[3px] not-has-disabled:has-not-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] sm:text-sm dark:bg-input/32 dark:has-aria-invalid:ring-destructive/24 dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-white/8%)]",
        className,
      ) || undefined}
      data-size={size}
      data-slot="textarea-control"
    >
      <FieldPrimitive.Control
        ref={ref}
        value={props.value}
        defaultValue={props.defaultValue}
        disabled={props.disabled}
        id={props.id}
        name={props.name}
        render={(
          defaultProps: React.ComponentProps<"textarea">,
        ) => (
          <textarea
            className={cn(
              "field-sizing-content min-h-17.5 w-full rounded-[inherit] px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)] outline-none max-sm:min-h-20.5",
              size === "sm" &&
                "min-h-16.5 px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1)-1px)] max-sm:min-h-19.5",
              size === "lg" &&
                "min-h-18.5 py-[calc(--spacing(2)-1px)] max-sm:min-h-21.5",
            )}
            data-slot="textarea"
            {...mergeProps(defaultProps, props)}
          />
        )}
      />
    </span>
  );
}

export { FieldPrimitive };
